import { z } from 'zod';

import { ExampleResult } from './exercise.js';
import { SourceCode, SourceLanguage } from './project.js';
import { CodeLanguage } from './languages.js';

/**
 * Points and rewards (ADR 0010). Private to each browser (owner token), decided on the server.
 * Everything a client needs to explain a reward — how many points, what unlocks what — lives
 * here so both sides agree.
 */

export const POINTS = {
  /** Solving an exercise, by difficulty 1–3 (once per exercise). */
  solve: [10, 20, 30] as const,
  /** Extra for solving without asking the tutor for a hint on that project (half the solve). */
  noHintBonus: [5, 10, 15] as const,
  /** First practice of the day (a finished run or a solve). */
  daily: 5,
  /** Streak milestones, once each. */
  streak: { 3: 10, 7: 25, 14: 50, 30: 100 } as Record<number, number>,
  /** Learning actions. `step` and `live` count toward LEARNING_DAILY_CAP. */
  step: 2,
  lang: 5,
  live: 10,
};

/** Most points learning actions (step, live) can earn in one day, so they cannot be farmed. */
export const LEARNING_DAILY_CAP = 20;

/** Minutes in a live session before it counts. */
export const LIVE_MINUTES = 5;

/** Total points needed for level n (level 1 starts at 0): 0, 50, 150, 300, 500, 750, … */
export function levelThreshold(level: number): number {
  return 25 * level * (level - 1);
}

export function levelFor(points: number): number {
  let level = 1;
  while (points >= levelThreshold(level + 1)) level++;
  return level;
}

export const RewardKind = z.enum(['solve', 'no-hint', 'daily', 'streak', 'step', 'lang', 'live']);
export type RewardKind = z.infer<typeof RewardKind>;

export const RewardEvent = z.object({
  id: z.string(),
  kind: RewardKind,
  points: z.number().int(),
  /** Exercise slug, language, streak length… depending on the kind. */
  detail: z.string().nullable(),
  at: z.string(),
});
export type RewardEvent = z.infer<typeof RewardEvent>;

// ---- badges

export const BadgeId = z.enum([
  'first-solve',
  'solver-10',
  'hard-one',
  'no-hint-5',
  'category-sequences',
  'category-conditions',
  'category-loops',
  'category-digits',
  'category-arrays',
  'category-strings',
  'all-exercises',
  'streak-3',
  'streak-7',
  'streak-30',
  'polyglot',
  'stepper',
  'live-learner',
]);
export type BadgeId = z.infer<typeof BadgeId>;

// ---- unlockables (cosmetic only)

export const AVATAR_COLORS = [
  { id: 'clay', color: '#D9663A', level: 1 },
  { id: 'moss', color: '#3E9C6E', level: 1 },
  { id: 'sky', color: '#5B7DB1', level: 2 },
  { id: 'plum', color: '#8C6FBF', level: 3 },
  { id: 'rose', color: '#C4688A', level: 4 },
  { id: 'teal', color: '#2E8C80', level: 5 },
  { id: 'amber', color: '#C98B4A', level: 6 },
  { id: 'ink', color: '#3C4046', level: 7 },
] as const;

export const PROFILE_ICONS = [
  { id: 'pebble', level: 1, badge: null },
  { id: 'star', level: 2, badge: null },
  { id: 'bolt', level: 3, badge: null },
  { id: 'rocket', level: 4, badge: null },
  { id: 'crown', level: 6, badge: null },
  { id: 'flame', level: 1, badge: 'streak-7' },
  { id: 'brain', level: 1, badge: 'no-hint-5' },
  { id: 'globe', level: 1, badge: 'polyglot' },
] as const satisfies readonly { id: string; level: number; badge: BadgeId | null }[];

/**
 * Block colour themes: logic, loops, math, variables, text, in/out, lists — in that order, which
 * is the toolbox order. `classic` is the mockups' palette; the others are unlocked by level.
 */
export const BLOCK_THEMES = [
  {
    id: 'classic',
    level: 1,
    colors: ['#5B7DB1', '#4F9A6A', '#8C6FBF', '#C98B4A', '#C4688A', '#3E9C8E', '#7A8CA8'],
  },
  {
    id: 'ocean',
    level: 3,
    colors: ['#3C6E9E', '#2F8F9D', '#5D6FC2', '#3B8BC2', '#7A62B0', '#23857A', '#4E7F94'],
  },
  {
    id: 'forest',
    level: 5,
    colors: ['#6B7F3A', '#3F7D4E', '#8A6E3C', '#A0703A', '#9A5B6B', '#4E8A6B', '#77804F'],
  },
  {
    id: 'sunset',
    level: 7,
    colors: ['#B5526B', '#D07A3A', '#9A4E9E', '#C9913A', '#D0607A', '#C0703E', '#A8636E'],
  },
  {
    id: 'neon',
    level: 9,
    colors: ['#4D6BFF', '#1FA870', '#9B4DFF', '#E08A00', '#E0457B', '#0F9FB0', '#5F7FC7'],
  },
] as const;

export type AvatarColorId = (typeof AVATAR_COLORS)[number]['id'];
export type ProfileIconId = (typeof PROFILE_ICONS)[number]['id'];
export type BlockThemeId = (typeof BLOCK_THEMES)[number]['id'];

export const Equipped = z.object({
  avatarColor: z.enum(AVATAR_COLORS.map((c) => c.id) as [AvatarColorId, ...AvatarColorId[]]),
  icon: z.enum(PROFILE_ICONS.map((i) => i.id) as [ProfileIconId, ...ProfileIconId[]]),
  blockTheme: z.enum(BLOCK_THEMES.map((t) => t.id) as [BlockThemeId, ...BlockThemeId[]]),
});
export type Equipped = z.infer<typeof Equipped>;

export const DEFAULT_EQUIPPED: Equipped = {
  avatarColor: 'clay',
  icon: 'pebble',
  blockTheme: 'classic',
};

/** Whether a cosmetic is unlocked for this level and set of badges. */
export function isUnlocked(
  item: { level: number; badge?: BadgeId | null },
  level: number,
  badges: ReadonlySet<BadgeId>,
): boolean {
  return level >= item.level && (!item.badge || badges.has(item.badge));
}

// ---- API

export const RewardsSummary = z.object({
  points: z.number().int(),
  level: z.number().int(),
  levelStart: z.number().int(),
  nextLevelAt: z.number().int(),
  streak: z.object({
    current: z.number().int(),
    best: z.number().int(),
    /** Whether today (the student's local date) already counts. */
    today: z.boolean(),
  }),
  badges: z.array(z.object({ id: BadgeId, at: z.string() })),
  recent: z.array(RewardEvent),
  equipped: Equipped,
});
export type RewardsSummary = z.infer<typeof RewardsSummary>;

export const RewardActionRequest = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('step'), projectId: z.string().max(64) }),
  z.object({ kind: z.literal('lang'), lang: CodeLanguage }),
]);
export type RewardActionRequest = z.infer<typeof RewardActionRequest>;

export const CheckRequest = z.object({
  workspace: z.record(z.string(), z.unknown()),
  /** In code mode the server parses this instead, so grading follows what the student typed. */
  source: SourceCode.nullable().optional(),
  sourceLang: SourceLanguage.nullable().optional(),
});
export type CheckRequest = z.infer<typeof CheckRequest>;

export const CheckResponse = z.object({
  passed: z.number().int(),
  total: z.number().int(),
  solved: z.boolean(),
  /** Per-example outcome, so the editor can show which example failed and why. */
  results: z.array(ExampleResult),
});
export type CheckResponse = z.infer<typeof CheckResponse>;

/** The student's calendar date, sent with every request so streaks follow their day. */
export const LOCAL_DATE_HEADER = 'x-local-date';
